import { useCallback, useEffect, useMemo, useState } from 'react';
import { Stack, Typography } from '@mui/material';
import { getPaychecksAnd106Forms, IPaychecks } from 'API/Paychecks/paychecks';
import { CustomMetaTitle } from 'Components/CustomMetaTitle';
import { LinkReturnToMain } from 'Components/LinkReturnToMain';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useAppSelector, useDictionary, useIsDesktop } from 'Hooks';
import { PaychecksList } from './PaychecksList';
import { HeaderTabs } from './HeaderTabs';
import { PaychecksAlert } from './PaychecksAlert';
import { NoPaychecksFound } from './NoPaychecksFound';
import { useLoader } from 'Hooks/useLoader';
import { useParams } from 'react-router-dom';

const tab106Id = '1';

export const Paychecks = () => {
  const [tabValue, setTabValue] = useState(0);
  const [paychecks, setPaychecks] = useState<IPaychecks>({
    forms106: [],
    payslips: [],
  });
  const dictionary = useDictionary(Dictionary, DICTIONARY.Paychecks);
  const { isLoading, setIsLoading, loader } = useLoader();
  const isDesktop = useIsDesktop();
  const params = useParams();

  useEffect(() => {
    if (params.tabId === tab106Id) setTabValue(1);
  }, [params]);

  const handleTabChange = (
    event: React.ChangeEvent<{}>,
    newValue: React.SetStateAction<number>
  ) => {
    setTabValue(newValue);
  };

  const imposterId = useAppSelector(({ imposter: { imposterId } }) => {
    return imposterId;
  });

  const getData = useCallback(async () => {
    setIsLoading(true);
    const paychecks = await getPaychecksAnd106Forms();
    setIsLoading(false);
    if (paychecks?.data) {
      setPaychecks(paychecks.data);
    }
    setIsLoading(false);
  }, [setIsLoading]);

  const isTabEmpty: boolean = useMemo(() => {
    const paychecksList =
      tabValue === 0 ? paychecks?.payslips : paychecks.forms106;

    if (!paychecksList) return true;

    return paychecksList.length === 0;
  }, [paychecks, tabValue]);

  useEffect(() => {
    getData();
  }, [getData, imposterId]);

  if (isLoading) return loader;

  if (isTabEmpty)
    return (
      <NoPaychecksFound tabValue={tabValue} handleTabChange={handleTabChange} />
    );

  return (
    <>
      <CustomMetaTitle title={dictionary.title} />
      <Stack gap={isDesktop ? '56px' : '24px'} marginBottom={'80px'}>
        <LinkReturnToMain />
        <Stack gap={isDesktop ? '48px' : '40px'} maxWidth='920px'>
          <Stack gap={isDesktop ? '40px' : '32px'}>
            <Stack
              gap={isDesktop ? '24px' : '16px'}
              color='text.secondary'
              minWidth='327px'
            >
              <Typography variant='h2' component='h1'>
                {dictionary.title}
              </Typography>
              <Typography variant='body3'>
                {dictionary.subtitle.lineOne}
              </Typography>
              <PaychecksAlert />
            </Stack>
            <HeaderTabs handleTabChange={handleTabChange} tabValue={tabValue} />
          </Stack>
          <PaychecksList tabValue={tabValue} data={paychecks} />
        </Stack>
      </Stack>
    </>
  );
};
